import { API } from '../../api';
import { Button, Card, Checkbox, Col, Collapse, Row } from 'antd';
import { Network } from 'vis-network';
import { HL7resourcesAll, nodeColors } from '../../consts';
import { _getMappingData, handleError, containsObject } from '../../utils/util';
import React, { createRef } from 'react';

const { Panel } = Collapse;
const CheckboxGroup = Checkbox.Group;
const excludeResources = ['CodeSystem', 'StructureDefinition', 'ValueSet'];

const options = {
  autoResize: true,
  height: '800px',
  width: '100%',
  physics: false,
  nodes: {
    fixed: {
      y: false,
      x:false
    },
    widthConstraint: {
      maximum: 250,
    },
  },
  edges: {
    smooth: false
  },
};

export default class SummaryQA extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      nodes: [],
      edges: [],
      mappings: [],
      mappingsDict: {},
      selectedFiles: {},
      checkedFileList: {},
      checkedFileListAll: {},
      allFiles: {},
      showCards: false,
      loading: false,
      colors: {},
      colorList: nodeColors,
    };
    this.network = {};
    this.appRef = createRef();
  }

  componentDidMount = async () => {
    await this.setState({ loading: true });
    await this.getData();
    this.renderGraph();
    const tempResDict = this.state.mappingsDict;
    const tempFilesDict = this.state.selectedFiles;
    const tempMappingsDict = {};
    const tempFilesDictAll = this.state.allFiles;
    const checkedList = this.state.checkedFileList;

    for (let resourceType of this.state.mappings) {
      if (resourceType.value.length > 0) {
        const tempFileList = [];
        const tempList = [];
        const resType = resourceType.value;
        tempMappingsDict[resType] = this.state.mappingsDict[resType]? this.state.mappingsDict[resType] : [];
        for(let namespace of resourceType.children){
          for (let file of namespace.children) {
            const fileName = file.value;
            await _getMappingData(this.props.client.id, 'latest', resType, namespace.value, fileName).then(
              res => {
                const mapData = res.data.mappings;
                const maps = this.getMappingsDict(mapData);
                if (!tempFileList.includes(fileName)){
                  tempFileList.push(fileName);
                  tempList.push({
                    label: fileName,
                    value: `${resType}|${fileName}`,
                    style: { width: '100%', display: 'wrap', marginLeft: 0, marginRight: 0 },
                  });
                }
                tempMappingsDict[resType].push(maps);
              },
            );
          }
        }

        tempResDict[resType] = { files: tempFileList };
        checkedList[resType] = tempList;
        tempFilesDictAll[resType] = tempFileList;
      }
    }

    await this.setState({
      mappingsDict: tempMappingsDict,
      allFiles: tempFilesDict,
      checkedFileList: checkedList,
      checkedFileListAll: checkedList,
    });
    this.setState({
      showCards: true,
      loading: false,
    });
  };

  getData = async () => {
    await API.get('mapping/resourcetypes', { params: { client_id: this.props.client.id } })
      .then(res => {
        this.setState({ mappings: res.data.resourcetypes });
      })
      .catch(err => handleError(err));
  };

  getColor = async currKey => {
    const colorsDict = this.state.colors;
    let { colorList } = this.state;
    const resource = currKey.split(':')[0];

    for (const [key, value] of Object.entries(this.state.colors)) {
      if (resource === key) {
        return value;
      }
    }

    colorsDict[resource] = colorList[0];
    colorList = colorList.slice(1);
    await this.setState({ colors: colorsDict, colorList }, () => {});

    return colorsDict[resource];
  };

  generateGraph = async () => {
    const { selectedFiles } = this.state;
    const selectedResources = Object.keys(selectedFiles);
    const nodes = [];
    const edges = [];
    await this.setState({
      nodes: [],
      edges: [],
    });

    for (let key in selectedFiles) {
      if (selectedFiles[key].length === 0) {
        let file = Object.keys(selectedFiles[key])[0]
        const currentNode = `${key}:\n${file}`;
        if (!containsObject(nodes, currentNode, "id") && typeof currentNode === 'string') {
          const node = {
            id: currentNode,
            label: currentNode,
            color: await this.getColor(currentNode),
            shape: 'box',
          };
          nodes.push(node);
        }
      }
      for (let file in selectedFiles[key]) {
        const currentNode = `${key}:\n${file}`;
        if (!containsObject(nodes, currentNode, "id") && typeof currentNode === 'string') {
          const node = {
            id: currentNode,
            label: currentNode,
            color: await this.getColor(currentNode),
            shape: 'box',
          };
          nodes.push(node);
        }

        for (let ref in selectedFiles[key][file]) {
          const row = selectedFiles[key][file][ref];
          let value = row.value.replace(/['"]+/g, '').split('/')[0];

          // check if referenced resource doesn't have a node yet
          for (let res in selectedResources) {
            if (value.includes(selectedResources[res])) {
              const file = Object.keys(selectedFiles[selectedResources[res]])[0];
              value = `${selectedResources[res]}:\n${file}`;
            }
          }

          if (!containsObject(nodes, value, "id") && typeof currentNode === 'string') {
            const node = {
              id: value,
              label: value,
              color: await this.getColor(value),
              shape: 'box',
            };
            nodes.push(node);
          }

          let label = row.path.split('[')[0];
          label = label.split('.')[0];
          const edge = {
            from: currentNode,
            to: value,
            label,
            length: key.length * 25,
            font: { align: 'middle', size: 12 },
            physics: true,
            arrows: 'to'
          };
          let isUniqueEdge = true;

          for (let e of edges) {
            if (e.to === edge.to && e.from === edge.from) {
              isUniqueEdge = false;

              if (!e.label.endsWith('(...)')) {
                e.label = `${e.label}(...)`;
              }
            }
          }

          if (isUniqueEdge || edges.length === 0) {
            edges.push(edge);
          }

        }
          this.setState({
            nodes,
            edges,
          });
      }
    }

    this.renderGraph();
  };

  getMappingsDict(mappings) {
    const mappingsDict = {};
    for (let i in mappings) {
      if (mappings[i].hasOwnProperty('file') && typeof mappings[i] === 'object') {
        for (let j in HL7resourcesAll) {
          const {file} = mappings[i];

          if (!excludeResources.includes(HL7resourcesAll[j])) {
            const startStr = `${HL7resourcesAll[j]}/`;

            if (!(file in mappingsDict)) {
              mappingsDict[file] = [];
            }

            if (typeof mappings[i].value === 'string') {
              if (mappings[i].value.startsWith(startStr)){
                mappingsDict[file].push({
                  path: mappings[i].path,
                  value: mappings[i].value,
                  resourceType: mappings[i].resourcetype,
                  file: mappings[i].file,
                });
              } else if (mappings[i].value.includes(startStr)){
                mappingsDict[file].push({
                  path: mappings[i].path,
                  value: HL7resourcesAll[j],
                  resourceType: mappings[i].resourcetype,
                  file: mappings[i].file,
                });
              }
            }
          }
        }
      }
    }

    return mappingsDict;
  }

  getResourceType(resource) {
    let resourceType = resource.resourcetype;
    const type = resource.resourceName.split(' ').slice(-1);

    if (resourceType === 'ExplanationOfBenefit') {
      resourceType = `${resourceType}?type.coding.code=${type}`;
    }

    return resourceType;
  }

  renderGraph() {
    this.network = new Network(
      this.appRef.current,
      {
        nodes: this.state.nodes,
        edges: this.state.edges,
      },
      options,
    );
  }

  onChange = async list => {
    if (list.length > 0) {
      const tempFileDict = this.state.selectedFiles;
      const key = list[0].split('|')[0];
      const files = [];
      tempFileDict[key] = [];

      for (let i in list) {
        const file = list[i].split('|')[1];
        tempFileDict[key][file] = [];

        for (let j in this.state.mappingsDict[key]) {
          for (let [dictKey, value] of Object.entries(this.state.mappingsDict[key][j])) {
            if (dictKey === file) {
              tempFileDict[key][file] = value;
            }
          }
        }

        files.push(list[i].split('|')[1]);
      }

      await this.setState({
        selectedFiles: tempFileDict,
      });
    }
  };

  clearAll = async () => {
    await this.setState({
      showCards: false,
      nodes: [],
      edges: [],
      selectedFiles: {},
      checkedFileList: this.state.checkedFileListAll,
    });
    await this.renderGraph();
    await this.setState({
      showCards: true,
    });
  };

  onRender = async () => {
    await this.generateGraph();
  };

  render() {
    const layout = {
      labelCol: { span: 6 },
      wrapperCol: { span: 18 },
    };
    return (
      <>
        <Button
          type="primary"
          style={{ marginBottom: 10, marginRight: 15 }}
          onClick={this.onRender}
          loading={this.state.loading}
        >
          Render
        </Button>
        <Button
          type="primary"
          style={{ marginBottom: 10, marginRight: 15 }}
          onClick={this.clearAll}
        >
          Clear
        </Button>
        <Row gutter={[24, 16]}>
          <Col span={4}>
            <Card title="Files" className="supported-resources">
              {this.state.showCards &&
                Object.keys(this.state.checkedFileList).map(resourceName => {
                  return (
                    <>
                      {(
                        <Collapse accordion>
                          <Panel header={resourceName} key={resourceName} style={{width: '100%', overflow:'hidden'}}>
                            <CheckboxGroup
                              style={{width: '100%', overflow:'hidden'}}
                              options={this.state.checkedFileList[resourceName]}
                              onChange={this.onChange}
                            />
                          </Panel>
                        </Collapse>
                      )}
                    </>
                  );
                })}
            </Card>
          </Col>

          <Col span={20}>
            <Card>
              <div ref={this.appRef} />
            </Card>
          </Col>
        </Row>
      </>
    );
  }
}
